import React, { useState, useEffect } from 'react';
import {
  History as HistoryIcon,
  Calendar,
  User,
  Package,
  Clock,
  Search,
  X,
  Loader,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  MapPin
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { pickingService } from '../../../../services/pickingService';
import { PListDto, PListDetailsDto } from '../../../../types/picking';
import { BackButton } from '../../../common/BackButton';
import { useOperatorDisplayNames } from '../../../../hooks/useOperatorDisplayNames';

interface HistoryAppProps {
  onBack: () => void;
}

export const HistoryApp: React.FC<HistoryAppProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { getDisplayName } = useOperatorDisplayNames();
  const { user } = useAuth();
  const { currentWarehouse } = useWarehouse();
  const [history, setHistory] = useState<PListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Filter states
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [showAllOperators, setShowAllOperators] = useState(false);

  // Expandable lines states
  const [expandedPListId, setExpandedPListId] = useState<number | null>(null);
  const [plistDetails, setPlistDetails] = useState<Map<number, PListDetailsDto>>(new Map());
  const [loadingLines, setLoadingLines] = useState<number | null>(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const from = dateFrom ? new Date(dateFrom) : undefined;
      const to = dateTo ? new Date(dateTo + 'T23:59:59') : undefined;

      const data = await pickingService.getPickingListHistory(
        undefined, // Let backend use current operator or all based on showAllOperators
        from,
        to,
        showAllOperators,
        currentWarehouse || undefined
      );

      setHistory(data);
    } catch (err) {
      console.error('Error loading history:', err);
      setError(t('warehouse.modules.history.completedPickingLists.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadHistory();
  };

  const handleReset = () => {
    setDateFrom('');
    setDateTo('');
    setSearchText('');
    setShowAllOperators(false);
    // Reload will happen on next render
  };

  const toggleExpand = async (pListId: number) => {
    if (expandedPListId === pListId) {
      // Collapse
      setExpandedPListId(null);
    } else {
      // Expand
      setExpandedPListId(pListId);

      // Load lines if not already loaded
      if (!plistDetails.has(pListId)) {
        setLoadingLines(pListId);
        try {
          const details = await pickingService.getPickingDetails(pListId);
          setPlistDetails(prev => new Map(prev).set(pListId, details));
        } catch (err) {
          console.error('Error loading lines:', err);
          setError(t('warehouse.modules.history.completedPickingLists.errors.loadLinesFailed'));
        } finally {
          setLoadingLines(null);
        }
      }
    }
  };

  useEffect(() => {
    if (!loading) {
      loadHistory();
    }
  }, [showAllOperators]);

  // Filter by search text (client-side filtering)
  const filteredHistory = history.filter((plist) => {
    if (!searchText.trim()) return true;

    const search = searchText.toLowerCase();
    const pListId = plist.pListID.toString();
    const desc1 = (plist.description1 || '').toLowerCase();
    const desc2 = (plist.description2 || '').toLowerCase();
    const operatorId = (plist.operatorID || '').toLowerCase();
    // Søket må treffe både kode og navn når lista viser navnet (TW 36353248) —
    // ellers slutter et søk på «Lager9» å virke uten at noen skjønner hvorfor.
    const operatorName = getDisplayName(plist.operatorID).toLowerCase();

    return pListId.includes(search) ||
           desc1.includes(search) ||
           desc2.includes(search) ||
           operatorId.includes(search) ||
           operatorName.includes(search);
  });

  // Set default date to today if not set
  useEffect(() => {
    if (!dateFrom && !dateTo) {
      const today = new Date();
      const oneWeekAgo = new Date(today);
      oneWeekAgo.setDate(today.getDate() - 7);

      setDateFrom(oneWeekAgo.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <BackButton onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg" iconSize={20} label="" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900 flex items-center">
                <HistoryIcon className="h-5 w-5 mr-2 text-gray-600" />
                {t('warehouse.modules.history.completedPickingLists.title')}
              </h1>
              <p className="text-sm text-gray-500">{t('warehouse.modules.history.completedPickingLists.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
              {filteredHistory.length} {t('warehouse.modules.history.completedPickingLists.pickingListsCount')}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4 max-w-6xl mx-auto space-y-4">
        {/* Filter Section */}
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <h2 className="font-semibold mb-3 flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-blue-500" />
            {t('common.buttons.filter')}
          </h2>

          <div className="space-y-3">
            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('warehouse.modules.history.completedPickingLists.fromDate')}
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('warehouse.modules.history.completedPickingLists.toDate')}
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Search Text */}
            <div className="relative">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t('warehouse.modules.history.completedPickingLists.searchPlaceholder')}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Show All Operators Checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="showAllOperators"
                checked={showAllOperators}
                onChange={(e) => setShowAllOperators(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="showAllOperators" className="text-sm text-gray-700">
                {t('warehouse.modules.history.completedPickingLists.showAllOperators')}
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSearch}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>{t('common.status.loading')}</span>
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    <span>{t('common.buttons.search')}</span>
                  </>
                )}
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>{t('common.buttons.reset')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* History List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b">
            <h2 className="font-semibold flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
              {t('warehouse.modules.history.completedPickingLists.title')} ({filteredHistory.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <Loader className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
              <p className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.loadingHistory')}</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>{t('warehouse.modules.history.completedPickingLists.noCompletedPickingLists')}</p>
              <p className="text-sm">{t('warehouse.modules.history.completedPickingLists.tryAdjustingFilters')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredHistory.map((plist) => {
                const isExpanded = expandedPListId === plist.pListID;
                const details = plistDetails.get(plist.pListID);
                const isLoadingLines = loadingLines === plist.pListID;

                return (
                  <div key={plist.id}>
                    <div
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleExpand(plist.pListID)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 flex items-start space-x-3">
                          {/* Expand/Collapse Icon */}
                          <div className="pt-1">
                            {isExpanded ? (
                              <ChevronDown className="h-5 w-5 text-gray-500" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-gray-500" />
                            )}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="font-semibold text-lg">
                                {t('warehouse.modules.history.completedPickingLists.pickingListHash')}{plist.pListID}
                              </div>
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                                {plist.statusText || t('warehouse.modules.history.completedPickingLists.completed')}
                              </span>
                            </div>

                            <div className="space-y-1 text-sm text-gray-600">
                              {plist.description1 && (
                                <div>{plist.description1}</div>
                              )}
                              {plist.description2 && (
                                <div>{plist.description2}</div>
                              )}

                              <div className="flex items-center space-x-4 mt-2">
                                <span className="flex items-center">
                                  <User className="h-4 w-4 mr-1" />
                                  {getDisplayName(plist.operatorID) || t('common.labels.unknown')}
                                </span>

                                <span className="flex items-center">
                                  <Package className="h-4 w-4 mr-1" />
                                  {plist.numOfOrderLines || 0} {t('warehouse.modules.history.completedPickingLists.lines')}
                                </span>

                                {plist.weight && (
                                  <span>
                                    {plist.weight.toFixed(2)} kg
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                                {plist.startWorkDateTime && (
                                  <span className="flex items-center">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {t('warehouse.modules.history.completedPickingLists.started')}: {pickingService.formatDateTime(plist.startWorkDateTime)}
                                  </span>
                                )}
                                {plist.stopWorkDateTime && (
                                  <span className="flex items-center">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    {t('warehouse.modules.history.completedPickingLists.completed')}: {pickingService.formatDateTime(plist.stopWorkDateTime)}
                                  </span>
                                )}
                              </div>

                              {plist.deliveryDate && (
                                <div className="text-xs text-gray-500">
                                  {t('warehouse.modules.history.completedPickingLists.deliveryDate')}: {pickingService.formatDate(plist.deliveryDate)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {plist.priority && plist.priority !== 100 && (
                          <div className="ml-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              plist.priority < 100
                                ? 'bg-red-100 text-red-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {t('warehouse.modules.history.completedPickingLists.priority')}: {plist.priority}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expanded Lines Section */}
                    {isExpanded && (
                      <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
                        {isLoadingLines ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader className="h-5 w-5 animate-spin text-blue-500 mr-2" />
                            <span className="text-sm text-gray-500">{t('warehouse.modules.history.completedPickingLists.loadingLines')}</span>
                          </div>
                        ) : details ? (
                          <div>
                            <div className="mb-2 text-sm font-medium text-gray-700">
                              {t('warehouse.modules.history.completedPickingLists.lines')} ({details.lines.length})
                            </div>
                            <div className="space-y-2">
                              {details.lines.map((line) => (
                                <div
                                  key={line.id}
                                  className="bg-white rounded p-3 border border-gray-200"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-1">
                                        <span className="font-mono text-sm font-semibold">
                                          {line.itemNo}
                                        </span>
                                        {line.orderNoERP && (
                                          <span className="text-xs text-gray-500">
                                            {t('warehouse.modules.history.completedPickingLists.order')}: {line.orderNoERP}
                                          </span>
                                        )}
                                      </div>

                                      {line.customerName && (
                                        <div className="text-xs text-gray-600 mb-1">
                                          {line.customerName}
                                        </div>
                                      )}

                                      <div className="flex items-center space-x-3 text-xs text-gray-500">
                                        <span className="flex items-center">
                                          <MapPin className="h-3 w-3 mr-1" />
                                          {line.binCode || t('warehouse.modules.history.completedPickingLists.noLocation')}
                                        </span>
                                        <span>
                                          {t('warehouse.general.quantityShort')}: {line.qtyPicked} / {line.qty}
                                        </span>
                                      </div>
                                    </div>

                                    {line.isCompleted && (
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 text-center py-4">
                            {t('warehouse.modules.history.completedPickingLists.noLinesFound')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
